import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  deleteDoc,
  query,
  where,
  getFirestore,
} from "firebase/firestore";
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Paper,
} from "@mui/material";
import dayjs from "dayjs";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useUser } from "../UserContext";

export default function MyBookingsMemberSide() {
  const db = getFirestore();
  const { memberData } = useUser();

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  useEffect(() => {
    if (!memberData) return;
    fetchMyBookings();
  }, [memberData]);

  const fetchMyBookings = async () => {
    try {
      setLoading(true);
      const bookingsRef = collection(db, "Bookings");
      const qy = query(
        bookingsRef,
        where("members", "array-contains", memberData.squareCustomerId)
      );
      const snap = await getDocs(qy);

      let allBookings = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      allBookings = allBookings.filter((b) => !isBookingEnded(b));

      allBookings.sort((a, b) => {
        const aStart = dayjs(`${a.date} ${a.startTime}`, "YYYY-MM-DD HH:mm");
        const bStart = dayjs(`${b.date} ${b.startTime}`, "YYYY-MM-DD HH:mm");
        return aStart.diff(bStart);
      });

      setBookings(allBookings);
    } catch (err) {
      console.error("Error fetching my bookings:", err);
    }
    setLoading(false);
  };

  const handleRowClick = (booking) => {
    setSelectedBooking(booking);
    setDetailDialogOpen(true);
  };

  const closeDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedBooking(null);
  };

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
  };

  const confirmCancelBooking = async () => {
    if (!selectedBooking) return;
    try {
      const docRef = doc(db, "Bookings", selectedBooking.id);
      await deleteDoc(docRef);

      alert("Booking deleted successfully.");
      setCancelDialogOpen(false);
      setDetailDialogOpen(false);
      setSelectedBooking(null);

      fetchMyBookings();
    } catch (err) {
      console.error("Delete booking error:", err);
      alert("Failed to delete booking.");
    }
  };

  const handleRequestNewCode = async () => {
    if (!selectedBooking) return;
    try {
      const setPasscodeForBookingCall = httpsCallable(
        functions,
        "setPasscodeForBookingCall"
      );
      const response = await setPasscodeForBookingCall({
        bookingId: selectedBooking.id,
      });

      if (response.data.success) {
        alert(`New code: ${response.data.newCode}`);

        const updated = { ...selectedBooking };
        updated.supervisorPasscode = response.data.newCode;
        updated.passcodeSet = true;
        if (response.data.expiresAt) {
          updated.passcodeExpiresAt = new Date(response.data.expiresAt);
        }
        setSelectedBooking(updated);
      } else {
        alert(response.data.message || "Failed to set new passcode");
      }
    } catch (err) {
      console.error("Error requesting new code:", err);
      alert("Error requesting new code.");
    }
  };

  const renderBookingsTable = () => {
    if (bookings.length === 0) {
      return <Typography>No bookings found.</Typography>;
    }

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>End Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.map((b) => {
              const dateDisplay = dayjs(b.date, "YYYY-MM-DD").format("DD-MM-YYYY");
              const startStr = dayjs(b.startTime, "HH:mm").format("hh:mm A");
              const endStr = dayjs(b.endTime, "HH:mm").format("hh:mm A");

              return (
                <TableRow
                  key={b.id}
                  hover
                  style={{ cursor: "pointer" }}
                  onClick={() => handleRowClick(b)}
                >
                  <TableCell>{dateDisplay}</TableCell>
                  <TableCell>{startStr}</TableCell>
                  <TableCell>{endStr}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Court Bookings
      </Typography>

      {loading ? <Typography>Loading bookings...</Typography> : renderBookingsTable()}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={closeDetailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Booking Details</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1">
                Date: {dayjs(selectedBooking.date, "YYYY-MM-DD").format("DD-MM-YYYY")}
              </Typography>
              <Typography variant="subtitle1">
                Time:{" "}
                {dayjs(selectedBooking.startTime, "HH:mm").format("hh:mm A")} -{" "}
                {dayjs(selectedBooking.endTime, "HH:mm").format("hh:mm A")}
              </Typography>
              <Typography variant="subtitle1">
                Courts: {selectedBooking.courtsUsed}
              </Typography>
              <Typography variant="subtitle1">
                Paid: {selectedBooking.paid ? "Yes" : "No"}
              </Typography>

              {selectedBooking.passcodeSet && (
                <Box sx={{ mt: 2, p: 1, bgcolor: "#f4f4f4" }}>
                  <Typography variant="subtitle2">Door Access Code:</Typography>
                  <Typography sx={{ fontSize: "1.2rem", fontWeight: "bold" }}>
                    {selectedBooking.supervisorPasscode}
                  </Typography>
                  {selectedBooking.passcodeExpiresAt && (
                    <Typography variant="body2">
                      Expires:{" "}
                      {dayjs(
                        selectedBooking.passcodeExpiresAt.toDate?.() ??
                          selectedBooking.passcodeExpiresAt
                      ).format("hh:mm A - DD-MM-YYYY")}
                    </Typography>
                  )}
                </Box>
              )}

              {isBookingInFuture(selectedBooking) && (
                <Button
                  variant="outlined"
                  color="error"
                  sx={{ mt: 2, mr: 2 }}
                  onClick={handleCancelClick}
                >
                  Cancel Booking
                </Button>
              )}

              {isBookingInProgress(selectedBooking) &&
                selectedBooking.passcodeRequired && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={handleRequestNewCode}
                  >
                    Request Door Access Code
                  </Button>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={closeCancelDialog}>
        <DialogTitle>Cancel Booking?</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Box>
              <Typography sx={{ mb: 2 }}>
                Are you sure you want to delete this booking on{" "}
                {selectedBooking.date} (
                {selectedBooking.startTime} - {selectedBooking.endTime})?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If a refund is required, please do NOT cancel online.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog}>No</Button>
          <Button variant="contained" color="error" onClick={confirmCancelBooking}>
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

function isBookingEnded(booking) {
  if (!booking?.date || !booking?.endTime) return false;
  const endMoment = dayjs(`${booking.date} ${booking.endTime}`, "YYYY-MM-DD HH:mm");
  return endMoment.isBefore(dayjs());
}

function isBookingInFuture(booking) {
  if (!booking?.date || !booking?.startTime) return false;
  const startMoment = dayjs(`${booking.date} ${booking.startTime}`, "YYYY-MM-DD HH:mm");
  return startMoment.isAfter(dayjs());
}

function isBookingInProgress(booking) {
  if (!booking?.date || !booking?.startTime || !booking?.endTime) return false;
  
  const now = dayjs();

  const startMoment = dayjs(`${booking.date} ${booking.startTime}`, "YYYY-MM-DD HH:mm");
  const endMoment = dayjs(`${booking.date} ${booking.endTime}`, "YYYY-MM-DD HH:mm");

  const startMomentMinus15 = startMoment.subtract(15, 'minutes');

  return now.isAfter(startMomentMinus15) && now.isBefore(endMoment);
}
